/**
 * The identity that scopes this app's persisted state.
 *
 * Every keyspace `@authe/fe-commons/storage` hands out — saved views, cached tx
 * bodies, brand overrides, dashboard prefs — is scoped by this tuple, so a tenant
 * or user switch reads a different keyspace instead of leaking one identity's
 * state into another. Hand-building it per call site invites one site to omit a
 * field and quietly share a keyspace across tenants; there were three copies of
 * the literal before this existed.
 */
import { useSession } from "@authe/ui-core/session";
import type { StorageIdentity } from "@authe/fe-commons/storage";
import { APPLICATION_SLUG } from "@/config";

/** This app's storage identity for the live session (all-null when signed out). */
export function useStorageIdentity(): StorageIdentity {
  const { session } = useSession();
  return {
    app: APPLICATION_SLUG,
    orgId: session?.tenant.orgId ?? null,
    tenantId: session?.tenant.tenantId ?? null,
    userId: useCurrentUserId(),
  };
}

/**
 * The signed-in user's id — the value backend rows carry as `owner_user_id`.
 *
 * One expression, because there were two: `session.user.id` and
 * `session.tenant.userId`. They resolve to the same value in the normal case
 * (both come from `projectUser`) and diverge only when the tenant claim is
 * absent, where this one falls back to the token subject. Anything comparing
 * client-side identity against a row's owner should read it from here so the
 * two can't drift apart later.
 */
export function useCurrentUserId(): string | null {
  const { session } = useSession();
  return session?.user.id ?? null;
}
